import { Card, Form, Row, Col, Input, Select, Spin, message } from 'antd';
import { cardMargin, colPadding } from '../../../shared/globalStyling/style';
import React, { useState, useRef } from 'react';
import SingleImageUploader from '../../../shared/images/SingleImageUploader';
import { MenuItemTextColor } from '../../../shared/globalStyling/styledText';
import {
  defaultRoasFormat,
  roasFormat as roasFormatOptions,
} from '../../../../core/utils/constants/constants';
import { getTimezoneOptions } from '../../../shared/configs/timezone.configs';
import GlobalButton from '../../../shared/globalStyling/Button';

const { Option } = Select;
const OrgDetailSettings = ({
  name = '', //Org name to be displayed and modified on input
  description = '', //Org description to be displayed and modified on input
  website = '', //Org website to be displayed and modified on input
  roasFormat = defaultRoasFormat.key, //Org roas format to be displayed and modified on input
  logo, //Org logo to be displayed and modified on input
  onSubmit = () => {}, //Function to be called when the form is submitted
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false); //Set true to show spinner for this card
  const lastSubmittedValuesRef = useRef(null); //Track last submitted values

  return (
    <Card style={{ ...cardMargin }} title={'Details Settings'}>
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          name={'orgDetailSettingsForm'}
          onFinish={async (values) => {
            const currentValues = lastSubmittedValuesRef.current || {
              name,
              description,
              website,
              zone: 'UTC', // Timezone is locked to UTC
              roasFormat,
              logo: logo?.id,
            };

            const hasChanges = Object.keys(values).some((key) => {
              if (key === 'imageKey') return false;
              return values[key] !== currentValues[key];
            });

            if (!hasChanges) {
              message.destroy();
              message.info('No changes detected');
              return;
            }

            setIsLoading(true);
            try {
              await onSubmit(values);
              lastSubmittedValuesRef.current = { ...values };
              message.success('Organization Details Updated Successfully');
            } catch (err) {
              message.error('Error updating organization data');
            }
            setIsLoading(false);
          }}
          initialValues={{
            name,
            description,
            website,
            zone: 'UTC', // Timezone is locked to UTC
            roasFormat,
          }}
        >
          <Form.Item name="imageKey" hidden={true} />
          <Row style={{ paddingTop: '25px', paddingBottom: '25px' }}>
            <Col xs={24} lg={4}>
              <Row align="middle" justify="center">
                <Form.Item name="logo">
                  <SingleImageUploader
                    statekey="logo"
                    image={logo} //Due to the nature of the SingleImageUploader, we need to pass the image as a prop
                    onChange={(key, value) => {
                      //Single Image Uploader on change value is different than the forms expected output so we need to manually set the value
                      form.setFieldsValue({
                        [key]: value.id,
                      });
                      //Hidden value for image key. This is used to update the logo on the sidebar
                      form.setFieldsValue({
                        [`imageKey`]: value.key,
                      });
                    }}
                    isSubmitted={true}
                  />
                </Form.Item>
              </Row>
            </Col>
            <Col xs={24} lg={20} style={colPadding}>
              <Row type="flex" style={{ paddingBottom: '25px' }}>
                <Col xs={24} xl={20}>
                  <Form.Item label="Org Name" name="name">
                    <Input placeholder="Org name" />
                  </Form.Item>
                  <Form.Item label="Description" name="description">
                    <Input.TextArea
                      placeholder="Description about the organization"
                      maxLength={200}
                      showCount
                    />
                  </Form.Item>
                  <Row
                    type="flex"
                    style={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <Col xs={24} xl={11}>
                      <Form.Item
                        label="Website"
                        name="website"
                        rules={[
                          {
                            type: 'url',
                          },
                        ]}
                      >
                        <Input placeholder="Name of Website" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} xl={11}>
                      <Form.Item label="Timezone" name="zone">
                        <Select
                          placeholder="Select timezone"
                          showSearch
                          disabled
                          filterOption={(input, option) => {
                            if (!option || !option.children) return false;
                            const text = option.children.props?.text || '';
                            return text.toLowerCase().includes(input.toLowerCase());
                          }}
                        >
                          {Object.entries(
                            getTimezoneOptions().reduce((groups, tz) => {
                              if (!groups[tz.region]) {
                                groups[tz.region] = [];
                              }
                              groups[tz.region].push(tz);
                              return groups;
                            }, {})
                          ).map(([region, timezones]) => (
                            <Select.OptGroup key={region} label={region}>
                              {timezones.map((tz) => (
                                <Option key={tz.key} value={tz.key}>
                                  <MenuItemTextColor text={tz.displayLabel} />
                                </Option>
                              ))}
                            </Select.OptGroup>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row
                    type="flex"
                    style={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <Col xs={24} xl={11}>
                      <Form.Item label="ROAS Format" name="roasFormat">
                        <Select>
                          {roasFormatOptions.map((option) => {
                            return (
                              <Option key={option.key} value={option.key}>
                                <MenuItemTextColor text={option.name} />
                              </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Col style={{ textAlign: 'right' }} xs={24}>
            <GlobalButton htmlType="submit" type="primary">
              Update
            </GlobalButton>
          </Col>
        </Form>
      </Spin>
    </Card>
  );
};

export default OrgDetailSettings;
